import React from 'react'
import { FormattedMessage } from 'react-intl'
import {
  SectionHeading,
  SectionDot,
  Card,
  CardSubtitle,
  TwoColumnRow,
  ImpactHeaderRow,
  InlineLegend,
  InlineLegendItem,
  InlineLegendDot,
  ThreatRow,
  ThreatRowLabel,
  ThreatRowBar,
  ResponseGrid,
  Wrapper
} from './style'
import Donutchartcard from '../Donutchartcard'
import ThreatImpactBar from '../ThreatImpactBar'
import ResponseGaugeCard from '../ResponseGaugeCard'
import ActionsDashboard from '../ActionsDashboard'
import AlertIconQuestion from '../AlertIconQuestion'
import { IMPACT_LEGEND, STATE_CATEGORY_CONFIG, THREAT_IMPACT_CONFIG, TIMING_CONFIG } from '../../constants'

const EvaluationTab = ({ monitoring }) => {
  const stateCounts = monitoring?.evaluationStateCategoryCounts || {}

  const stateCategorySegments = STATE_CATEGORY_CONFIG.map((item) => ({
    ...item,
    value: stateCounts[item.key] || 0
  }))

  const totalStateCount = stateCategorySegments.reduce((sum, seg) => sum + seg.value, 0)
  const displayStateSegments =
    totalStateCount === 0
      ? [{ key: 'noData', fill: '#e5e7eb', labelId: null, labelDefaultMessage: 'No data', value: 1 }]
      : stateCategorySegments

  const pressureData = monitoring?.evaluationPressure || {}
  const impactCounts = pressureData?.impactCounts || {}
  const timingCounts = pressureData?.timingCounts || {}
  const threatRows = pressureData?.threatRows || []

  const threatImpactSegments = THREAT_IMPACT_CONFIG.map((item) => ({
    ...item,
    value: impactCounts[item.key] || 0
  }))

  const totalThreatImpactCount = threatImpactSegments.reduce((sum, seg) => sum + seg.value, 0)
  const displayThreatImpactSegments =
    totalThreatImpactCount === 0
      ? [{ key: 'noData', fill: '#e5e7eb', labelId: null, labelDefaultMessage: 'No data', value: 1 }]
      : threatImpactSegments

  const timingSegments = TIMING_CONFIG.map((item) => ({
    ...item,
    value: timingCounts[item.key] || 0
  }))

  const totalTimingCount = timingSegments.reduce((sum, seg) => sum + seg.value, 0)
  const displayTimingSegments =
    totalTimingCount === 0
      ? [{ key: 'noData', fill: '#e5e7eb', labelId: null, labelDefaultMessage: 'No data', value: 1 }]
      : timingSegments

  const responseData = monitoring?.evaluationResponse || {}
  const mgmt = responseData.managementPlanning || { level: 'none', descriptionDefaultMessage: null }
  const cons = responseData.conservationActions || { level: 'none', descriptionDefaultMessage: null }
  const pa = responseData.coverageProtectedAreas || { level: 'none', descriptionDefaultMessage: null }
  const oecm = responseData.coverageOECMs || { level: 'none', descriptionDefaultMessage: null }

  const getStateMedianColor = () => {
    if (monitoring?.state_evaluation === false) return '#9ca3af'
    if (!totalStateCount || totalStateCount === 0) return '#9ca3af'
    const medianTarget = (totalStateCount + 1) / 2
    let accumulated = 0
    for (const seg of stateCategorySegments) {
      accumulated += seg.value
      if (accumulated >= medianTarget) {
        return seg.fill
      }
    }
    return '#9ca3af'
  }

  const stateDotColor = getStateMedianColor()

  const getPressureMedianColor = () => {
    if (monitoring?.pressure_evaluation === false) return '#9ca3af'
    if (!totalThreatImpactCount || totalThreatImpactCount === 0) return '#9ca3af'
    const medianTarget = Math.ceil(totalThreatImpactCount / 2)
    let accumulated = 0
    for (const seg of threatImpactSegments) {
      accumulated += seg.value
      if (accumulated >= medianTarget) {
        return seg.fill
      }
    }
    return '#9ca3af'
  }

  const pressureDotColor = getPressureMedianColor()

  const getResponseDotColor = () => {
    if (monitoring?.response_evaluation === false) return '#9ca3af'
    const paLevel = pa.level || 'none'
    const oecmLevel = oecm.level || 'none'
    let combinedPaOecm = 'whole'
    if (paLevel === 'none' && oecmLevel === 'none') {
      combinedPaOecm = 'none'
    } else if ([paLevel, oecmLevel].includes('none')) {
      combinedPaOecm = 'little'
    } else if ([paLevel, oecmLevel].some((l) => ['little', 'some', 'most'].includes(l))) {
      combinedPaOecm = 'some'
    }

    const levels = [mgmt.level || 'none', cons.level || 'none', combinedPaOecm]
    const hasData = levels.some((l) => l && l !== 'none')
    if (!hasData) return '#9ca3af'

    if (levels.includes('none')) return '#ef4444'
    if (levels.some((l) => ['little', 'some', 'most'].includes(l))) return '#f59e0b'
    return '#22c55e'
  }

  const responseDotColor = getResponseDotColor()

  return (
    <Wrapper data-test="monitoring-evaluation-tab">
      <SectionHeading>
        <SectionDot color={stateDotColor} />
        <FormattedMessage id="monitoringResultsStateTitle" defaultMessage="State" />
      </SectionHeading>
      <div>
        <Card>
          <CardSubtitle>
            <FormattedMessage
              id="monitoringResultsStateSubtitle"
              defaultMessage="State category that best describes the % remaining"
            />
          </CardSubtitle>
          <Donutchartcard
            segments={displayStateSegments}
            legendSegments={stateCategorySegments}
            legendPosition="left"
            size="lg"
            noBorder
          />
        </Card>
      </div>

      <SectionHeading>
        <SectionDot color={pressureDotColor} />
        <FormattedMessage id="monitoringResultsPressureTitle" defaultMessage="Pressure" />
      </SectionHeading>
      <div>
        <TwoColumnRow>
          <Donutchartcard
            titleId="monitoringResultsThreatImpact"
            titleDefaultMessage="Threat impact"
            segments={displayThreatImpactSegments}
            legendSegments={threatImpactSegments}
            legendPosition="right"
          />
          <Donutchartcard
            titleId="monitoringResultsTiming"
            titleDefaultMessage="Timing"
            segments={displayTimingSegments}
            legendSegments={timingSegments}
            legendPosition="right"
          />
        </TwoColumnRow>
      </div>

      <SectionHeading>
        <SectionDot color={pressureDotColor} />
        <FormattedMessage id="monitoringResultsThreatImpactTitle" defaultMessage="Threat impact" />
      </SectionHeading>
      <div>
        <Card style={{ padding: 0 }}>
          <ImpactHeaderRow>
            <FormattedMessage id="monitoringImpactLabel" defaultMessage="Impact:" />
            <InlineLegend>
              {IMPACT_LEGEND.map(({ key, fill, labelId, labelDefaultMessage }) => (
                <InlineLegendItem key={key}>
                  <FormattedMessage id={labelId} defaultMessage={labelDefaultMessage} />
                  <InlineLegendDot color={fill} />
                </InlineLegendItem>
              ))}
            </InlineLegend>
          </ImpactHeaderRow>

          {threatRows.map((row, index) => (
            <ThreatRow key={`${row.key}-${index}`}>
              <ThreatRowLabel>
                {row.nameId ? (
                  <FormattedMessage id={row.nameId} defaultMessage={row.nameDefaultMessage} />
                ) : (
                  row.nameDefaultMessage
                )}
              </ThreatRowLabel>
              <ThreatRowBar>
                <ThreatImpactBar
                  total={row.total}
                  segments={[
                    { key: 'low', value: row.low, color: '#22c55e' },
                    { key: 'medium', value: row.medium, color: '#f59e0b' },
                    { key: 'high', value: row.high, color: '#f97316' },
                    { key: 'veryHigh', value: row.veryHigh, color: '#ef4444' }
                  ]}
                />
              </ThreatRowBar>
            </ThreatRow>
          ))}
        </Card>
      </div>

      <SectionHeading>
        <SectionDot color={responseDotColor} />
        <FormattedMessage id="monitoringResultsResponseTitle" defaultMessage="Response" />
        <AlertIconQuestion title={<FormattedMessage id="monitoringTooltipEvaluationResponse" defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris." />} />
      </SectionHeading>
      <div>
        <ResponseGrid>
          <ResponseGaugeCard
            titleId="monitoringGaugeManagementPlanning"
            titleDefaultMessage="Extent of management planning"
            helpTextId="monitoringGaugeManagementPlanningHelp"
            helpTextDefaultMessage="How complete and up to date the management plan is"
            level={mgmt.level}
            descriptionId={null}
            descriptionDefaultMessage={mgmt.descriptionDefaultMessage}
          />
          <ResponseGaugeCard
            titleId="monitoringGaugeConservationActions"
            titleDefaultMessage="Extent of conservation actions"
            helpTextId="monitoringGaugeConservationActionsHelp"
            helpTextDefaultMessage="How comprehensively and effectively conservation actions are implemented"
            level={cons.level}
            descriptionId={null}
            descriptionDefaultMessage={cons.descriptionDefaultMessage}
          />
          <ResponseGaugeCard
            titleId="monitoringGaugeCoverageProtectedAreas"
            titleDefaultMessage="Coverage by protected areas"
            helpTextId="monitoringGaugeCoverageProtectedAreasHelp"
            helpTextDefaultMessage="Share of the site covered by formally protected areas"
            level={pa.level}
            descriptionId={null}
            descriptionDefaultMessage={pa.descriptionDefaultMessage}
          />
          <ResponseGaugeCard
            titleId="monitoringGaugeCoverageOECMs"
            titleDefaultMessage="Coverage by OECMs"
            helpTextId="monitoringGaugeCoverageOECMsHelp"
            helpTextDefaultMessage="Share of the site covered by other effective area-based conservation measures"
            level={oecm.level}
            descriptionId={null}
            descriptionDefaultMessage={oecm.descriptionDefaultMessage}
          />
        </ResponseGrid>
        <ActionsDashboard response={monitoring?.site_responses?.[0]} monitoringId={monitoring?.id} />
      </div>
    </Wrapper>
  )
}

export default EvaluationTab
